import clamp from 'lodash-es/clamp';
import { useCallback, useRef } from 'react';
var LAYER_ROW_HEIGHT = 26;
var DRAG_CURSOR_HEIGHT = 2;
var MOUSE_MOVE_DELAY_PX = 5;
var CONTAINER_SCROLL_MULTIPLIER = 2;
export function useLayersDrag(onDragToTarget) {
    var dropzoneCursorRef = useRef(null);
    var listContainerRef = useRef(null);
    var mouseDownPoint = useRef(null);
    var dropTarget = useRef(null);
    var mouseMoveAcc = useRef(0);
    var onMouseMoveListener = useCallback(function (e) {
        if (mouseMoveAcc.current < MOUSE_MOVE_DELAY_PX) {
            mouseMoveAcc.current += Math.abs(e.movementX + e.movementY);
            return;
        }
        var scrollRegion = getScrollRegion(listContainerRef.current, e.clientX, e.clientY);
        var shouldScrollUp = scrollRegion === 'top' && e.movementY < 0;
        var shouldScrollDown = scrollRegion === 'bottom' && e.movementY > 0;
        if (shouldScrollDown || shouldScrollUp) {
            listContainerRef.current.scrollBy({
                top: e.movementY * CONTAINER_SCROLL_MULTIPLIER,
            });
        }
        var target = getDropTarget(listContainerRef.current, e.clientX, e.clientY);
        if (target) {
            dropTarget.current = target;
            var elem = target.elem, direction = target.direction;
            var y = direction === 'in-front'
                ? elem.offsetTop - 3
                : elem.offsetTop + 5 + LAYER_ROW_HEIGHT;
            var style = {
                height: "".concat(DRAG_CURSOR_HEIGHT, "px"),
                width: "".concat(elem.offsetWidth, "px"),
                display: 'block',
                transform: "translateY(".concat(y, "px)"),
            };
            Object.assign(dropzoneCursorRef.current.style, style);
        }
    }, []);
    var onDragEnd = useCallback(function () {
        if (dropTarget.current && onDragToTarget) {
            onDragToTarget(dropTarget.current);
        }
        mouseMoveAcc.current = 0;
        dropTarget.current = null;
        dropzoneCursorRef.current.style.display = 'none';
    }, [onDragToTarget]);
    var onMouseDown = useCallback(function (itemId, event) {
        // Do not initiate drag if ctrl/meta keys are pressed.
        // Multi-select of layers takes precedence over drag.
        if (event.ctrlKey || event.metaKey) {
            return;
        }
        mouseDownPoint.current = [event.clientX, event.clientY];
        function onMouseUpListener(e) {
            onDragEnd(e);
            window.removeEventListener('mousemove', onMouseMoveListener);
            window.removeEventListener('mouseup', onMouseUpListener);
        }
        if (onDragToTarget) {
            window.addEventListener('mousemove', onMouseMoveListener);
            window.addEventListener('mouseup', onMouseUpListener);
        }
    }, [onDragToTarget, onDragEnd, onMouseMoveListener]);
    return {
        dropzoneCursorRef: dropzoneCursorRef,
        listContainerRef: listContainerRef,
        onMouseDown: onMouseDown,
    };
}
/**
 * Given the `mouseX` and `mouseY` positions, returns `top` or `bottom` if
 * the mouse is above of below the container respectively. Thie means the container
 * should scroll in the given direction. Otherwise, returns `null` if the mouse isn't
 * in the scroll regions.
 */
function getScrollRegion(container, mouseX, mouseY) {
    var SCROLL_REGION_THRESHOLD = 30;
    var containerRect = container.getBoundingClientRect();
    var yRelativeToTop = mouseY - containerRect.top;
    var yRelativeToBottom = mouseY - containerRect.top - containerRect.height;
    // Above the top point of the container
    if (yRelativeToTop < SCROLL_REGION_THRESHOLD)
        return 'top';
    // Below the bottom point of the container
    if (yRelativeToBottom > -SCROLL_REGION_THRESHOLD)
        return 'bottom';
    return null;
}
function getDropTarget(container, mouseX, mouseY) {
    var containerRect = container.getBoundingClientRect();
    // Clamp the mouseX value so that drag can continue when mouse is outside the
    // bounds of the layers panel.
    var clampedX = clamp(mouseX, containerRect.left, containerRect.width);
    var elemAtPoint = document.elementFromPoint(clampedX, mouseY);
    var hoveredElem = elemAtPoint && elemAtPoint.closest('[data-layerid]');
    if (hoveredElem) {
        var relativeY = mouseY - containerRect.top;
        var midLine = hoveredElem.offsetTop + LAYER_ROW_HEIGHT / 2;
        var direction = relativeY > midLine ? 'behind' : 'in-front';
        var itemId = hoveredElem.getAttribute('data-layerid');
        return { elem: hoveredElem, direction: direction, itemId: itemId };
    }
    return null;
}
